"use client";

import { MarketMode } from "@decibeltrade/sdk";
import { useTranslations } from "next-intl";
import { JSX } from "react";
import { create } from "zustand";

import { Tooltip } from "@/components/tooltip";
import { SignalStrength } from "@/components/vectors/connection-indicator";
import { env } from "@/env";
import { useIsOnline } from "@/hooks/use-is-online";
import { useVisibleAndOnlinePoll } from "@/hooks/use-visible-and-online-poll";
import { isBrowserNetworkFetchError } from "@/utils/error";
import { Logger } from "@/utils/logger";

import { readDexSdk } from "./dex-sdk";

export const MODERATE_LATENCY_THRESHOLD = 5_000;
export const HIGH_LATENCY_THRESHOLD = 7_500;
export const VERY_HIGH_LATENCY_THRESHOLD = 10_000;
export const CRITICAL_LATENCY_THRESHOLD = 12_000;
export const LATENCY_HISTORY_SIZE = 3;

/** `all_market_prices` covers the whole tape, so 30s of silence is a dead feed, not a quiet market. */
export const PRICE_FEED_STALE_MS = 30_000;

export interface AppHealthState {
  latency: number;
  latencyHistory: number[];
  isHighLatencyBannerShown: boolean;
  setLatency: (latency: number) => void;
  isFullNodeDown: boolean;
  setIsFullNodeDown: (isFullNodeDown: boolean) => void;
  isMissingMarkets: boolean;
  setIsMissingMarkets: (isMissingMarkets: boolean) => void;
  isMissingMarketDepth: boolean;
  setIsMissingMarketDepth: (isMissingMarketDepth: boolean) => void;
  isPriceFeedStale: boolean;
  setIsPriceFeedStale: (isPriceFeedStale: boolean) => void;
  currentMarketMode: MarketMode;
  setCurrentMarketMode: (currentMarketMode: MarketMode) => void;
}

export const useAppHealthState = create<AppHealthState>((set, get) => ({
  latency: 0,
  latencyHistory: [],
  isHighLatencyBannerShown: false,
  setLatency: (latency) => {
    const state = get();
    const newHistory = [...state.latencyHistory, latency].slice(-LATENCY_HISTORY_SIZE);
    const averageLatency = newHistory.reduce((sum, val) => sum + val, 0) / newHistory.length;

    let shouldShowBanner = state.isHighLatencyBannerShown;
    if (!state.isHighLatencyBannerShown && averageLatency > CRITICAL_LATENCY_THRESHOLD) {
      shouldShowBanner = true;
    } else if (state.isHighLatencyBannerShown && averageLatency < VERY_HIGH_LATENCY_THRESHOLD) {
      shouldShowBanner = false;
    }

    set({
      latency,
      latencyHistory: newHistory,
      isHighLatencyBannerShown: shouldShowBanner,
    });
  },
  isFullNodeDown: false,
  setIsFullNodeDown: (isFullNodeDown) => set({ isFullNodeDown }),
  isMissingMarkets: false,
  setIsMissingMarkets: (isMissingMarkets) => set({ isMissingMarkets }),
  isMissingMarketDepth: false,
  isPriceFeedStale: false,
  setIsPriceFeedStale: (isPriceFeedStale) => set({ isPriceFeedStale }),
  setIsMissingMarketDepth: (isMissingMarketDepth) => set({ isMissingMarketDepth }),
  currentMarketMode: "Open",
  setCurrentMarketMode: (currentMarketMode) => set({ currentMarketMode }),
}));

const checkFullNodeHealth = async () => {
  const { isFullNodeDown, setIsFullNodeDown } = useAppHealthState.getState();
  try {
    // Send the API key so this probe uses the keyed quota instead of the tiny
    // anonymous per-IP limit, and treat 429 as healthy — a rate-limited node
    // responded, so the network is reachable.
    const apiKey = env.NEXT_PUBLIC_API_KEY;
    const res = await fetch(
      readDexSdk.config.fullnodeUrl,
      apiKey ? { headers: { Authorization: `Bearer ${apiKey}` } } : undefined,
    );
    if (res.ok || res.status === 429) {
      if (isFullNodeDown) {
        setIsFullNodeDown(false);
      }
    } else {
      if (!isFullNodeDown) {
        setIsFullNodeDown(true);
      }
    }
  } catch (error) {
    if (!isFullNodeDown) {
      setIsFullNodeDown(true);
      if (!isBrowserNetworkFetchError(error)) {
        Logger.error(error, {
          message: "Full node is down",
          fullnodeUrl: readDexSdk.config.fullnodeUrl,
        });
      }
    }
  }
};

export function PollFullNodeHealth() {
  useVisibleAndOnlinePoll(checkFullNodeHealth, { intervalMs: 60_000 });
  return null;
}

export interface CalculateSignalStrengthArgs {
  latency: number;
  isFullNodeDown: boolean;
  isPriceFeedStale?: boolean;
}

export const calculateSignalStrength = ({
  latency,
  isFullNodeDown,
  isPriceFeedStale = false,
}: CalculateSignalStrengthArgs): SignalStrength => {
  if (isFullNodeDown) {
    return "none";
  }

  if (latency > CRITICAL_LATENCY_THRESHOLD) {
    return "none";
  }

  // Frozen prices are a degraded connection however good the node latency is.
  if (isPriceFeedStale) {
    return "low";
  }

  if (latency > VERY_HIGH_LATENCY_THRESHOLD) {
    return "low";
  } else if (latency > HIGH_LATENCY_THRESHOLD) {
    return "okay";
  } else if (latency > MODERATE_LATENCY_THRESHOLD) {
    return "good";
  } else {
    return "excellent";
  }
};

export const useIsNetworkDown = () => {
  /** TODO:
   * Add additional criteria to determine if the network is down.
   * Using latency of the WS data is possibly too aggressive, but if no WS messages are received at all,
   * or if the HTTP API is not responding, then it's probably enough to claim that the network is down.
   */

  const isFullNodeDown = useAppHealthState((state) => state.isFullNodeDown);
  const isOnline = useIsOnline();
  return isFullNodeDown || !isOnline;
};

export interface NetworkDownTooltipProps {
  isNetworkDown: boolean;
  children: JSX.Element;
  anchorElement?: HTMLDivElement | HTMLButtonElement | null;
}

export function NetworkDownTooltip({
  isNetworkDown,
  children,
  anchorElement,
}: NetworkDownTooltipProps) {
  const t = useTranslations("warningBanner");
  return (
    <Tooltip
      content={t("networkDownWarning")}
      disabled={!isNetworkDown}
      anchorElement={anchorElement}
    >
      {children}
    </Tooltip>
  );
}
